import { useEffect, useState } from "react";
import api from "../lib/api";
import { LifecycleBadge, RiskBadge, KillSwitchBadge, ReadinessBar } from "../components/Badges";
import { ChevronRight, Activity, FileEdit, Power, Radio, Lightbulb, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { DashboardSkeleton } from "../components/Skeleton";
import { usePermissions } from "../hooks/usePermissions";
import ApprovalQueue from "../components/dashboard/ApprovalQueue";

export default function Dashboard() {
    const perm = usePermissions();
    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [criticalReports, setCriticalReports] = useState([]);
    const [intake, setIntake] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancel = false;
        const fetchAll = async () => {
            try {
                // Build Prompt #13 — each panel uses its own permission gate so Strategic
                // Oversight gets the SAME layout (minus action buttons).
                const [s, p, c, ci] = await Promise.all([
                    api.get("/dashboard/summary").then((r) => r.data),
                    api.get("/products").then((r) => r.data),
                    perm.viewFieldIntel
                        ? api.get("/dashboard/critical-field-reports").then((r) => r.data).catch(() => [])
                        : Promise.resolve([]),
                    perm.viewConceptIntake
                        ? api.get("/dashboard/concept-intake-summary").then((r) => r.data).catch(() => null)
                        : Promise.resolve(null),
                ]);
                if (cancel) return;
                setSummary(s);
                setProducts(p);
                setCriticalReports(c);
                setIntake(ci);
            } catch (err) {
                if (cancel) return;
                if (err?.response?.status !== 401) {
                    setError(err?.response?.data?.detail || err.message || "Failed to load dashboard");
                }
            } finally {
                if (!cancel) setLoading(false);
            }
        };
        fetchAll();
        return () => { cancel = true; };
    }, [perm.viewFieldIntel, perm.viewConceptIntake]);

    if (loading) {
        return <DashboardSkeleton />;
    }

    if (error || !summary) {
        return (
            <div className="border border-red-500/40 bg-red-500/5 p-6" data-testid="dashboard-error">
                <div className="overline text-red-400">Dashboard Unavailable</div>
                <div className="heading-display text-xl mt-2">Could not load governance state</div>
                <div className="text-xs text-snow-dim mt-2 font-mono">
                    {typeof error === "string" ? error : "Please retry."}
                </div>
            </div>
        );
    }

    const killSwitchProducts = summary.kill_switch_products || [];
    const recentActivity = summary.recent_activity || [];
    // Oversight can see queue-volume signals but not act on them. The dashboard treats
    // these two permissions distinctly per Build Prompt #13.
    const canSeeQueue = perm.approveGate || perm.viewDashboardOversight;
    const canActOnQueue = perm.approveGate;
    // Investigate target — if a single kill switch is active, link directly to its product.
    const investigateHref =
        killSwitchProducts.length === 1
            ? `/products/${killSwitchProducts[0].id}`
            : "/products";

    return (
        <div className="space-y-6" data-testid="dashboard-root">
            {/* Header */}
            <div className="flex items-end justify-between">
                <div>
                    <div className="overline">Command View / 01</div>
                    <h1 className="heading-display text-4xl mt-1">KITT Dashboard</h1>
                </div>
                {/* Manage Products link is permission-gated — hidden from Oversight et al. */}
                {perm.editProduct && (
                    <Link
                        to="/products"
                        data-testid="dashboard-view-products-btn"
                        className="bg-signal text-jet hover:bg-signal-hover px-5 py-2 transition-colors font-bold uppercase tracking-[0.2em] text-xs flex items-center gap-2"
                    >
                        Manage Products <ChevronRight className="w-3 h-3" />
                    </Link>
                )}
            </div>

            {/* KPI Grid (Build Prompt #13)
                Order: Total Products · Mission Ready · Avg Readiness · Kill Switches
                Kill Switches sits on the far right and now hosts the Investigate button. */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI label="Total Products" value={summary.total_products} testid="kpi-total" />
                <KPI label="Mission Ready" value={summary.mission_ready} accent testid="kpi-mission-ready" />
                <KPI label="Avg Readiness" value={`${summary.avg_readiness}%`} testid="kpi-avg-readiness" />
                <KPI
                    label="Kill Switches"
                    value={summary.kill_switches_active}
                    alert={summary.kill_switches_active > 0}
                    testid="kpi-kill-switches"
                    footer={
                        summary.kill_switches_active > 0 ? (
                            <Link
                                to={investigateHref}
                                data-testid="kill-switch-investigate-btn"
                                className="mt-3 inline-flex items-center gap-1 border border-signal text-signal hover:bg-signal hover:text-jet px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.2em] transition-colors"
                                title={killSwitchProducts.map((p) => p.name).join(" · ")}
                            >
                                Investigate <ChevronRight className="w-3 h-3" />
                            </Link>
                        ) : null
                    }
                />
            </div>

            {/* Distribution rows */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <DistributionPanel
                    title="Risk Tier Distribution"
                    data={summary.by_risk}
                    colors={{ Low: "#4CAF50", Medium: "#FFC107", High: "#FF6A00", Critical: "#FF3B30" }}
                />
                <DistributionPanel
                    title="Lifecycle Status"
                    data={summary.by_status}
                    colors={{ "Under Review": "#A0A0A0", "Mission Ready": "#FF6A00", Restricted: "#FFC107", Suspended: "#FF3B30", Retired: "#707070" }}
                />
            </div>

            {/* ===== Vertical body — Build Prompt #13 canonical order =====
                1. Approval Queue       (immediate action)
                2. Concept Intake       (innovation pipeline)
                3. Live Product Roster  (active fleet)
                4. Critical Field Reports (technical debt)
                5. Recent System Activity (audit stream) */}

            {/* 1. Approval Queue */}
            {canSeeQueue && <ApprovalQueue readOnly={!canActOnQueue} />}

            {/* 2. Concept Intake widget */}
            {perm.viewConceptIntake && intake && (intake.submitted_count > 0 || intake.total > 0) && (
                <Link
                    to="/concept-intake"
                    data-testid="concept-intake-widget"
                    className={`block border-2 transition-colors hover:bg-signal/10 ${
                        intake.submitted_count > 0 ? "border-signal bg-signal/5 animate-pulse-signal" : "border-[#2A2A2A] bg-jet-elev1"
                    }`}
                >
                    <div className="border-b border-signal/40 px-5 py-3 flex items-center justify-between bg-signal/5">
                        <div className="flex items-center gap-3">
                            <Lightbulb className="w-4 h-4 text-signal" />
                            <span className="overline text-signal">Concept Intake — Innovation Funnel</span>
                        </div>
                        <span className="text-[11px] text-snow-dim font-mono uppercase tracking-wider tabular-nums flex items-center gap-1">
                            Open queue <ChevronRight className="w-3 h-3" />
                        </span>
                    </div>
                    <div className="px-5 py-4 grid grid-cols-3 lg:grid-cols-5 gap-4">
                        <Stat label="Submitted" value={intake.submitted_count} alert={intake.submitted_count > 0} testid="intake-stat-submitted" />
                        <Stat label="Under Review" value={intake.under_review_count} testid="intake-stat-review" />
                        <Stat label="Info Needed" value={intake.more_info_count} testid="intake-stat-info" />
                        <Stat label="Approved" value={intake.approved_count} testid="intake-stat-approved" />
                        <Stat label="Archived" value={intake.archived_count} testid="intake-stat-archived" />
                    </div>
                    {intake.recent_submitted?.length > 0 && (
                        <div className="border-t border-signal/20 px-5 py-3 bg-jet-elev2">
                            <div className="overline mb-2">Latest Submissions</div>
                            <ul className="space-y-1.5">
                                {intake.recent_submitted.slice(0, 3).map((idea) => (
                                    <li key={idea.id} className="flex items-center gap-3 text-[12px] font-mono" data-testid={`intake-recent-${idea.idea_id}`}>
                                        <Inbox className="w-3 h-3 text-signal flex-shrink-0" />
                                        <span className="text-signal tabular-nums">{idea.idea_id}</span>
                                        <span className="text-snow truncate">{idea.title}</span>
                                        <span className="text-[10px] text-snow-muted ml-auto whitespace-nowrap">{idea.submitter_email}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Link>
            )}

            {/* 3. Live Product Roster — Build Prompt #16 filtered to Mission Ready only */}
            <div className="border border-[#2A2A2A] bg-jet-elev1" data-testid="products-snapshot-panel">
                <div className="flex items-center justify-between border-b border-[#2A2A2A] px-5 py-3">
                    <div className="flex items-center gap-3">
                        <Activity className="w-3 h-3 text-signal" />
                        <span className="overline">Live Product Roster — Mission Ready</span>
                    </div>
                    <Link to="/products" className="text-xs text-signal uppercase tracking-[0.2em] hover:underline">
                        View all →
                    </Link>
                </div>
                <table className="w-full" data-testid="dashboard-products-table">
                    <thead>
                        <tr className="border-b border-[#2A2A2A]">
                            <Th>Product</Th>
                            <Th>Type</Th>
                            <Th>Lifecycle</Th>
                            <Th>Risk</Th>
                            <Th className="w-[180px]">Readiness</Th>
                            <Th>Kill Switch</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            const live = products.filter((p) => p.lifecycle_status === "Mission Ready").slice(0, 8);
                            if (live.length === 0) {
                                return (
                                    <tr><td colSpan={6} className="px-5 py-6 text-center text-snow-muted text-xs font-mono" data-testid="dashboard-products-empty">
                                        No Mission-Ready products yet.
                                    </td></tr>
                                );
                            }
                            return live.map((p) => (
                            <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                <Td><div className="font-mono text-sm">{p.name}</div></Td>
                                <Td><span className="overline">{p.product_type}</span></Td>
                                <Td><LifecycleBadge status={p.lifecycle_status} /></Td>
                                <Td><RiskBadge tier={p.risk_tier} /></Td>
                                <Td><ReadinessBar value={p.readiness_score} /></Td>
                                <Td><KillSwitchBadge active={p.kill_switch_active} /></Td>
                            </tr>
                            ));
                        })()}
                    </tbody>
                </table>
            </div>

            {/* 4. Critical Field Reports */}
            {perm.viewFieldIntel && criticalReports.length > 0 && (
                <div
                    data-testid="critical-field-reports-panel"
                    className="border-2 border-signal bg-signal/5 animate-pulse-signal"
                >
                    <div className="border-b border-signal/40 px-5 py-3 flex items-center justify-between bg-signal/10">
                        <div className="flex items-center gap-3">
                            <Radio className="w-4 h-4 text-signal" />
                            <span className="overline text-signal">Critical Field Reports — Emergency Action</span>
                        </div>
                        <span className="text-xs font-mono text-signal tabular-nums">{criticalReports.length} unresolved</span>
                    </div>
                    <ul className="divide-y divide-signal/20">
                        {criticalReports.map((r) => (
                            <li key={r.id} data-testid={`critical-report-row-${r.report_id}`} className="px-5 py-3">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="font-mono text-sm text-signal tabular-nums font-bold">{r.report_id}</span>
                                    <Link
                                        to={`/products/${r.product_id}`}
                                        className="text-xs font-mono text-snow underline-offset-2 hover:underline"
                                        data-testid={`critical-report-product-${r.report_id}`}
                                    >
                                        {r.product_name}
                                    </Link>
                                    {r.kill_switch_active && (
                                        <span className="inline-flex items-center gap-1 border border-signal text-signal bg-signal/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em]">
                                            <Power className="w-3 h-3" /> Grounded
                                        </span>
                                    )}
                                    {r.active_recertification && (
                                        <span className="inline-flex items-center border border-signal text-signal bg-signal/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em]">
                                            Recert Active
                                        </span>
                                    )}
                                    <span className="text-[10px] text-snow-muted font-mono uppercase tracking-wider ml-auto">
                                        {r.hours_open}h open • {r.operator_email}
                                    </span>
                                </div>
                                <div className="text-xs text-signal mt-1 font-mono italic">"{r.issue_description}"</div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* 5. Recent System Activity — restricted to Super Admin + Chief of Operations per Build Prompt #14 */}
            {(perm.isSuperAdmin || perm.isChiefOfOps) && (
            <div className="border border-[#2A2A2A] bg-jet-elev1" data-testid="recent-activity-panel">
                <div className="flex items-center justify-between border-b border-[#2A2A2A] px-5 py-3">
                    <div className="flex items-center gap-3">
                        <Activity className="w-3 h-3 text-signal" />
                        <span className="overline">Recent System Activity</span>
                    </div>
                    <span className="text-xs font-mono text-snow-muted">{recentActivity.length} entries</span>
                </div>
                {recentActivity.length === 0 ? (
                    <div className="px-5 py-8 text-center text-snow-muted text-xs font-mono">
                        No audit entries yet. Kill-switch toggles, status changes, and template edits will appear here.
                    </div>
                ) : (
                    <ul className="divide-y divide-[#2A2A2A]">
                        {recentActivity.map((entry) => (
                            <li
                                key={entry.id}
                                data-testid={`activity-row-${entry.id}`}
                                className="px-5 py-3 flex items-start gap-4 hover:bg-white/[0.03] transition-colors"
                            >
                                <ActivityIcon type={entry.action_type} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <ActionBadge type={entry.action_type} />
                                        <span className="font-mono text-sm text-snow truncate">
                                            {entry.target_label || entry.target_id.slice(0, 8)}
                                        </span>
                                    </div>
                                    {entry.reason && (
                                        <div className="text-xs text-snow-dim mt-1 font-mono italic">"{entry.reason}"</div>
                                    )}
                                    {entry.metadata && entry.metadata.from && entry.metadata.to && (
                                        <div className="text-[11px] text-snow-muted mt-1 font-mono">
                                            {entry.metadata.from} → <span className="text-signal">{entry.metadata.to}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="text-right text-[11px] font-mono text-snow-muted whitespace-nowrap tabular-nums">
                                    <div>{entry.operator_email}</div>
                                    <div className="mt-0.5">{formatTime(entry.timestamp)}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            )}
        </div>
    );
}

function KPI({ label, value, accent, alert, testid, footer }) {
    return (
        <div
            data-testid={testid}
            className={`border ${alert ? "border-signal bg-signal/5" : "border-[#2A2A2A] bg-jet-elev1"} p-5 flex flex-col`}
        >
            <div className="overline">{label}</div>
            <div
                className={`heading-display text-4xl mt-3 tabular-nums ${
                    alert ? "text-signal" : accent ? "text-signal" : "text-snow"
                }`}
            >
                {value}
            </div>
            {footer}
        </div>
    );
}

function Stat({ label, value, alert, testid }) {
    return (
        <div data-testid={testid} className="text-center">
            <div className={`heading-display text-3xl tabular-nums ${alert ? "text-signal" : "text-snow"}`}>
                {value || 0}
            </div>
            <div className="overline mt-1">{label}</div>
        </div>
    );
}


function DistributionPanel({ title, data, colors }) {
    const total = Object.values(data).reduce((a, b) => a + b, 0) || 1;
    return (
        <div className="border border-hairline bg-jet-elev1 p-5">
            <div className="overline mb-4">{title}</div>
            <div className="space-y-3">
                {Object.entries(data).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-3">
                        <div className="w-32 text-xs font-mono text-snow-dim truncate">{k}</div>
                        <div className="flex-1 h-2 bg-jet-elev2 border border-hairline">
                            <div
                                className="h-full"
                                style={{ width: `${(v / total) * 100}%`, backgroundColor: colors[k] }}
                            />
                        </div>
                        <div className="w-8 text-right font-mono tabular-nums text-sm">{v}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Th({ children, className = "" }) {
    return (
        <th
            className={`text-left px-5 py-3 overline font-normal ${className}`}
        >
            {children}
        </th>
    );
}
function Td({ children }) {
    return <td className="px-5 py-3 align-middle">{children}</td>;
}

function ActivityIcon({ type }) {
    const map = {
        "Kill-Switch Engage": <Power className="w-4 h-4 text-signal" />,
        "Kill-Switch Release": <Power className="w-4 h-4 text-green-400" />,
        "Status Change": <ChevronRight className="w-4 h-4 text-yellow-400" />,
        "Template Edit": <FileEdit className="w-4 h-4 text-snow-dim" />,
    };
    return (
        <div className="w-7 h-7 border border-[#2A2A2A] flex items-center justify-center flex-shrink-0">
            {map[type] || <Activity className="w-4 h-4 text-snow-dim" />}
        </div>
    );
}

function ActionBadge({ type }) {
    const cls = type.startsWith("Kill-Switch")
        ? "border-signal/60 text-signal bg-signal/5"
        : type === "Status Change"
        ? "border-yellow-500/40 text-yellow-400"
        : "border-[#2A2A2A] text-snow-dim";
    return (
        <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${cls}`}>
            {type}
        </span>
    );
}

function formatTime(iso) {
    try {
        const d = new Date(iso);
        const now = new Date();
        const diff = (now - d) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
    } catch {
        return iso;
    }
}
